import { type FC, type MouseEvent } from "react";
import { Avatar, IconButton, Stack, Tooltip } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import TelegramIcon from "@mui/icons-material/Telegram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { buildShareHref, type ShareNetwork } from "../domains";

// Брендовые цвета из референсного ShareIcons.js; задизейбленное состояние —
// серые аватары + Tooltip с причиной (стрим не опубликован).
const NETWORKS: {
  network: ShareNetwork;
  label: string;
  color: string;
  Icon: typeof FacebookIcon;
}[] = [
  {
    network: "facebook",
    label: "Facebook",
    color: "#3b5998",
    Icon: FacebookIcon,
  },
  { network: "twitter", label: "Twitter", color: "#1da1f2", Icon: XIcon },
  {
    network: "telegram",
    label: "Telegram",
    color: "#009fda",
    Icon: TelegramIcon,
  },
  {
    network: "whatsapp",
    label: "WhatsApp",
    color: "#2bd146",
    Icon: WhatsAppIcon,
  },
];

interface ShareIconsProps {
  shareUrl: string;
  shareText: string;
  /** Непустая строка = кнопки выключены, показывается как Tooltip-причина */
  disabledText?: string | null;
}

export const ShareIcons: FC<ShareIconsProps> = ({
  shareUrl,
  shareText,
  disabledText,
}) => {
  const disabled = Boolean(disabledText);

  // Референсный ShareIcons.js: элементы — реальные <a href>, чтобы средний
  // клик/копирование ссылки работали как обычная share-ссылка; сама
  // share-логика (Web Share API либо popup) выполняется по клику.
  const handleShare = (
    network: ShareNetwork,
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    if (disabled) return;
    if (navigator.share) {
      void navigator.share({ url: shareUrl, text: shareText }).catch(() => {
        // отмена системного шэра пользователем — не ошибка
      });
      return;
    }
    window.open(
      buildShareHref(network, shareUrl, shareText),
      "_blank",
      "width=640,height=436"
    );
  };

  const icons = (
    <Stack direction="row" spacing={1} justifyContent="center">
      {NETWORKS.map(({ network, label, color, Icon }) => (
        <Tooltip key={network} title={label} arrow>
          <IconButton
            disabled={disabled}
            component="a"
            href={buildShareHref(network, shareUrl, shareText)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => handleShare(network, event)}
            sx={{ p: 0 }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: disabled ? "#757575" : color,
              }}
            >
              <Icon
                fontSize="small"
                sx={{ color: disabled ? "#424242" : "common.white" }}
              />
            </Avatar>
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );

  if (!disabled) return icons;

  return (
    <Tooltip title={disabledText}>
      <span>{icons}</span>
    </Tooltip>
  );
};
